import { memo, useCallback, useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { HeaderButton } from './Header.styles';
import type { HeaderProps } from './Header';
import { selectActiveHistoryId, selectHistory } from 'store/entities/chatbot/selectors';
import { setActiveHistoryId, setHistory } from 'store/entities/chatbot/actions';
import type { ChatHistory } from 'types';
import { filterChatHistoryByScope } from 'utils/chatbotHistory';
import { Close, Content, Count, Header, Item, ItemDate, ItemTitle, List, Message, Popover, RemoveButton } from './History.styles';

const HistoryBase = ({ historyScope, setMessages, setConversation, onConversationChange }: HeaderProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useSelector(selectHistory);
  const activeHistoryId = useSelector(selectActiveHistoryId);
  const scopedHistory = filterChatHistoryByScope(history, historyScope);

  const [historyAnchorEl, setHistoryAnchorEl] = useState<HTMLButtonElement | null>(null);
  const historyOpen = Boolean(historyAnchorEl);
  const historyId = historyOpen ? 'history-popover' : undefined;

  const handleClose = useCallback(() => setHistoryAnchorEl(null), []);

  const handleOpenMessage = useCallback(
    (chat: ChatHistory) => {
      onConversationChange();
      setMessages(chat.messages);
      setConversation({ assistantId: chat.assistantId });

      dispatch(setActiveHistoryId(chat.id));
      handleClose();
    },
    [dispatch, handleClose, onConversationChange, setConversation, setMessages]
  );

  const handleDeleteHistory = useCallback(
    (e: MouseEvent, id: string) => {
      e.stopPropagation();

      const updatedHistory = history.filter((chat) => chat.scope !== historyScope || chat.id !== id);
      dispatch(setHistory(updatedHistory));

      if (activeHistoryId === id) {
        onConversationChange();
        dispatch(setActiveHistoryId(null));
        setMessages([]);
      }

      if (!updatedHistory.some((chat) => chat.scope === historyScope)) {
        handleClose();
      }
    },
    [activeHistoryId, dispatch, handleClose, history, historyScope, onConversationChange, setMessages]
  );

  return (
    <>
      <HeaderButton title={t('user.chat.history')} onClick={(e) => setHistoryAnchorEl(e.currentTarget)} disabled={scopedHistory.length === 0}>
        🕰️{t('user.chat.history')} {scopedHistory.length > 0 && `(${scopedHistory.length})`}
      </HeaderButton>
      <Popover
        id={historyId}
        open={historyOpen}
        anchorEl={historyAnchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}>
        <Content>
          <Header>
            <strong>{t('user.chat.chatHistory')}</strong>
            <Close onClick={handleClose}>×</Close>
          </Header>
          <List>
            {scopedHistory
              .toSorted((a, b) => b.timestamp - a.timestamp)
              .map((chat) => (
                <Item key={chat.id} isActive={activeHistoryId === chat.id} onClick={() => handleOpenMessage(chat)}>
                  <div>
                    <ItemTitle>💬</ItemTitle>
                    <ItemDate>
                      {new Date(chat.timestamp).toLocaleString([], {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </ItemDate>
                    <RemoveButton title={t('user.chat.deleteHistory')} onClick={(e) => handleDeleteHistory(e, chat.id)}>
                      🗑️
                    </RemoveButton>
                  </div>
                  <Message>
                    {chat.messages[0]?.content.substring(0, 50)}
                    {chat.messages[0]?.content.length > 50 ? '...' : ''}
                  </Message>
                  <Count>
                    {chat.messages.length} {t('user.chat.messages')}
                  </Count>
                </Item>
              ))}
          </List>
        </Content>
      </Popover>
    </>
  );
};

export const History = memo(HistoryBase);
